import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaBed, FaUsers, FaCalendarAlt, FaMoneyBillWave, FaMapMarkerAlt, FaPhone, FaEnvelope, FaCheckCircle, FaTimesCircle, FaEdit, FaArrowLeft, FaWifi, FaCar, FaPaw, FaSwimmingPool, FaSnowflake, FaTv, FaUtensils, FaParking, FaConciergeBell, FaDumbbell, FaGamepad, FaBabyCarriage, FaFire, FaShieldAlt, FaTree, FaCoffee, FaWind, FaHotTub, FaShower, FaTrash, FaSave, FaChild, FaInfoCircle, FaRuler, FaTrain, FaBuilding, FaBath, FaLock } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { nightsBetween, formatDateSafe } from '../../../utils/dates';
import { decodeJWT } from '../../../utils/jwt';

const PageContainer = styled.div`
  padding: 40px;
  background: #f8f9fa;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 24px 12px 32px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 24px;
  transition: all 0.3s ease;

  &:hover {
    background: #f8f9fa;
    border-color: #ba824f;
    color: #ba824f;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 24px;
  margin-bottom: 24px;
`;

const PropertyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;

  h1 {
    font-size: 1.5rem;
    color: #333;
    margin: 0 0 8px 0;
    font-weight: 600;
  }

  .location {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #666;
    font-size: 0.875rem;
  }

  .actions {
    display: flex;
    gap: 12px;
  }

  .action-btn {
    padding: 8px 16px;
    border-radius: 6px;
    border: 1px solid #eee;
    background: white;
    cursor: pointer;
    font-size: 0.875rem;
    color: #666;
    transition: all 0.3s ease;

    &:hover {
      background: #f8f9fa;
      border-color: #ba824f;
      color: #ba824f;
    }
  }
`;

const CharacteristicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const CharacteristicItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #333;

  .icon {
    color: #ba824f;
    font-size: 1.2rem;
  }

  .value {
    font-weight: 600;
    color: #34574A;
  }
`;

const BookingSidebar = styled(Card)`
  position: sticky;
  top: 24px;
`;

const PriceSection = styled.div`
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 20px;

  .price {
    font-size: 1.5rem;
    font-weight: 700;
    color: #34574A;
    margin-bottom: 4px;
  }

  .period {
    color: #666;
    font-size: 0.875rem;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 20px;

  h3 {
    font-size: 0.875rem;
    color: #666;
    margin: 0 0 8px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  p {
    font-size: 0.875rem;
    color: #333;
    margin: 0;
  }
`;

const GuestSelector = styled.div`
  margin-bottom: 20px;

  h3 {
    font-size: 0.875rem;
    color: #666;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .guest-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .guest-item {
    text-align: center;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;

    .icon {
      font-size: 1.5rem;
      color: #ba824f;
      margin-bottom: 8px;
    }

    .count {
      font-size: 1.25rem;
      font-weight: 700;
      color: #333;
    }

    .label {
      font-size: 0.75rem;
      color: #666;
      margin-top: 4px;
    }
  }
`;

const EditableGuestField = styled.div`
  margin-bottom: 12px;

  label {
    display: block;
    font-size: 0.75rem;
    color: #666;
    margin-bottom: 4px;
  }

  .input-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #eee;
    border-radius: 6px;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: #ba824f;
    }
  }

  .btn-adjust {
    padding: 8px 12px;
    background: #34574A;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.875rem;

    &:hover {
      background: #2a4539;
    }
  }
`;

const MessageArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  margin-bottom: 20px;

  &:focus {
    outline: none;
    border-color: #ba824f;
  }
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &.primary {
    background: #34574A;
    color: white;

    &:hover {
      background: #2a4539;
    }
  }

  &.secondary {
    background: white;
    color: #666;
    border: 1px solid #eee;

    &:hover {
      background: #f8f9fa;
    }
  }

  &.danger {
    background: #c62828;
    color: white;

    &:hover {
      background: #b71c1c;
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  
  &.agendada {
    background: #e6f4ea;
    color: #1e7e34;
  }
  
  &.em-andamento {
    background: #fff3e0;
    color: #f57c00;
  }
  
  &.concluida {
    background: #e8eaf6;
    color: #3f51b5;
  }
  
  &.cancelada {
    background: #ffebee;
    color: #c62828;
  }
`;

const parseMoney = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value).replace(/[^\d,.-]/g, '');
  if (!raw) return null;
  const numeric = raw.includes(',')
    ? Number(raw.replace(/\./g, '').replace(',', '.'))
    : Number(raw);
  return Number.isNaN(numeric) ? null : numeric;
};

// Retorna o valor total da reserva: usa o campo da API quando existir,
// senão calcula noites x valor da diária do imóvel
const getReservationTotal = (reservation) => {
  const apiValue =
    reservation.valorRESERVA ??
    reservation.valortotalRESERVA ??
    reservation.valorTotalRESERVA ??
    reservation.totalRESERVA;
  const parsed = parseMoney(apiValue);
  if (parsed !== null) return parsed;
  const noites = Number(reservation.noitesRESERVA) || nightsBetween(reservation.deRESERVA, reservation.ateRESERVA);
  const diaria = parseMoney(reservation.imovel?.valorIMOVEL);
  if (noites > 0 && diaria !== null) return noites * diaria;
  return null;
};

const formatCurrency = (value, currencyISO) => {
  if (value === null || value === undefined) return '';
  const iso = currencyISO || 'EUR';
  const numeric = typeof value === 'number' ? value : parseMoney(value);

  if (numeric === null || Number.isNaN(numeric)) return '';
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: iso
    }).format(numeric);
  } catch {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'EUR'
    }).format(numeric);
  }
};

const getCharacteristicIcon = (name) => {
  const iconMap = {
    'Pessoas': FaUsers,
    'Camas de Solteiro': FaBed,
    'Camas de Casal': FaBed,
    'Lavanderia': FaShower,
    'Ar Condicionado': FaSnowflake,
    'Vaga de Garagem': FaCar,
    'Portaria': FaConciergeBell,
    'Próximo ao Metro': FaTrain,
    'Portão': FaLock,
    'Festas': FaCalendarAlt,
    'Sauna': FaHotTub,
    'Cobertura': FaBuilding,
    'Quartos': FaBed,
    'Banheiros': FaBath,
    'Wi-Fi': FaWifi,
    'Salão de Jogos': FaGamepad,
    'Playground': FaTree,
    'Elevador': FaBuilding,
    'Quadras Esportivas': FaDumbbell,
    'Academia': FaDumbbell,
    'Pets': FaPaw,
    'Área (m²)': FaRuler,
  };
  return iconMap[name] || FaCheckCircle;
};

const ReservationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingGuests, setEditingGuests] = useState(false);
  const [guestDetails, setGuestDetails] = useState({
    adultos: 0,
    criancas: 0,
    bebes: 0,
    pets: 0
  });

  const fetchReservation = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${import.meta.env.VITE_URL_API}/reservas/${id}`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Erro ao carregar reserva');

      const data = await response.json();
      setReservation(data);
      
      // Set guest details from reservation data if available
      if (data.hospedesRESERVA) {
        let raw = data.hospedesRESERVA;
        let parsed;
        try {
          parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch {
          parsed = null;
        }
        if (typeof parsed === 'number') {
          setGuestDetails({
            adultos: parsed,
            criancas: 0,
            bebes: 0,
            pets: 0
          });
        } else if (parsed && typeof parsed === 'object') {
          setGuestDetails({
            adultos: parsed.adultos || 0,
            criancas: parsed.criancas || 0,
            bebes: parsed.bebes || 0,
            pets: parsed.pets || 0
          });
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes da reserva');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReservation();
  }, [fetchReservation]);

  const handleUpdateGuests = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${import.meta.env.VITE_URL_API}/reservas/${id}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...reservation,
          hospedesRESERVA: JSON.stringify(guestDetails),
          adultosRESERVA: guestDetails.adultos,
          criancasRESERVA: guestDetails.criancas,
          bebesRESERVA: guestDetails.bebes,
          petsRESERVA: guestDetails.pets
        }),
      });

      if (!response.ok) throw new Error('Erro ao atualizar hóspedes');

      toast.success('Dados dos hóspedes atualizados com sucesso!');
      setEditingGuests(false);
      fetchReservation();
    } catch (error) {
      toast.error('Erro ao atualizar dados dos hóspedes');
      console.error(error);
    }
  };

  const handleDeleteReservation = async () => {
    const result = await Swal.fire({
      title: 'Excluir Reserva?',
      text: 'Tem certeza que deseja excluir esta reserva? Essa ação não poderá ser desfeita!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${import.meta.env.VITE_URL_API}/reservas/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      toast.success('Reserva excluída com sucesso!');
      navigate('/dashboard/reservations');
    } catch (error) {
      toast.error('Erro ao excluir reserva');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Carregando detalhes da reserva...
        </div>
      </PageContainer>
    );
  }

  if (!reservation) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Reserva não encontrada.
        </div>
      </PageContainer>
    );
  }

  const statusMap = {
    1: 'agendada',
    2: 'em-andamento',
    3: 'concluida',
    4: 'cancelada'
  };

  const statusLabel = {
    'agendada': 'Agendada',
    'em-andamento': 'Em Andamento',
    'concluida': 'Concluída',
    'cancelada': 'Cancelada'
  };

  const imovel = reservation.imovel || {};
  const characteristics = imovel.dadosBasicos?.caracteristicas || imovel.caracteristicas || [];

  // Quando o usuário logado é apenas o hóspede (não anfitrião/dono do imóvel),
  // a página fica somente leitura
  let myCodigo = null;
  try {
    const token = localStorage.getItem('auth_token');
    const payload = token ? decodeJWT(token) : null;
    myCodigo = payload?.codigo || payload?.id || payload?.userId || null;
  } catch {
    myCodigo = null;
  }
  const mine = myCodigo !== null ? String(myCodigo) : null;
  const isGuestOnly = !!mine &&
    String(reservation.usuarioRESERVA) === mine &&
    String(reservation.hostRESERVA) !== mine &&
    String(imovel?.clienteIMOVEL ?? '') !== mine &&
    String(imovel?.imobiliaria?.usuarioIMOBILIARIA ?? '') !== mine;

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/dashboard/reservations')}>
        <FaArrowLeft /> Voltar para Reservas
      </BackButton>

      <ContentGrid>
        <div>
          <PropertyHeader>
            <div>
              <h1>{imovel.tituloIMOVEL || 'Reserva sem título'}</h1>
              <div className="location">
                <FaMapMarkerAlt />
                {imovel.enderecoIMOVEL || 'Endereço não informado'}
              </div>
            </div>
            <div className="actions">
              <StatusBadge className={statusMap[reservation.statusRESERVA]}>
                {statusLabel[statusMap[reservation.statusRESERVA]]}
              </StatusBadge>
            </div>
          </PropertyHeader>

          <Card>
            <h2 style={{ fontSize: '1.25rem', marginBottom: '20px', color: '#333' }}>Características</h2>
            <CharacteristicsGrid>
              {characteristics.length > 0 ? (
                characteristics.map((char, index) => {
                  const Icon = getCharacteristicIcon(char.nome);
                  return (
                    <CharacteristicItem key={index}>
                      <Icon className="icon" />
                      <span>{char.nome}</span>
                      <span className="value">{char.quantidade}</span>
                    </CharacteristicItem>
                  );
                })
              ) : (
                <CharacteristicItem>
                  <FaInfoCircle className="icon" />
                  <span>Nenhuma característica informada</span>
                </CharacteristicItem>
              )}
            </CharacteristicsGrid>
          </Card>

          <Card>
            <h2 style={{ fontSize: '1.25rem', marginBottom: '20px', color: '#333' }}>Detalhes da Reserva</h2>
            <CharacteristicsGrid>
              <CharacteristicItem>
                <FaCalendarAlt className="icon" />
                <span>Check-in</span>
                <span className="value">{formatDateSafe(reservation.deRESERVA)}</span>
              </CharacteristicItem>
              <CharacteristicItem>
                <FaCalendarAlt className="icon" />
                <span>Check-out</span>
                <span className="value">{formatDateSafe(reservation.ateRESERVA)}</span>
              </CharacteristicItem>
              <CharacteristicItem>
                <FaCalendarAlt className="icon" />
                <span>Noites</span>
                <span className="value">{reservation.noitesRESERVA ?? (nightsBetween(reservation.deRESERVA, reservation.ateRESERVA) || '-')}</span>
              </CharacteristicItem>
              <CharacteristicItem>
                <FaMoneyBillWave className="icon" />
                <span>Valor Total</span>
                <span className="value">{formatCurrency(getReservationTotal(reservation), imovel.moedaIMOVEL)}</span>
              </CharacteristicItem>
              <CharacteristicItem>
                <FaCheckCircle className="icon" />
                <span>Status Pagamento</span>
                <span className="value">
                  {reservation.statuspagamentoRESERVA === 1 ? 'Pendente' : 
                   reservation.statuspagamentoRESERVA === 2 ? 'Pago' : 'Cancelado'}
                </span>
              </CharacteristicItem>
            </CharacteristicsGrid>
          </Card>

          {reservation.observacaoRESERVA && (
            <Card>
              <h2 style={{ fontSize: '1.25rem', marginBottom: '12px', color: '#333' }}>Mensagem do hóspede</h2>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#555', lineHeight: 1.5 }}>
                {reservation.observacaoRESERVA}
              </p>
            </Card>
          )}
        </div>

        <div>
          <BookingSidebar>
            <PriceSection>
              <div className="price">
                {formatCurrency(imovel.valorIMOVEL, imovel.moedaIMOVEL)}
              </div>
              <div className="period">por noite</div>
            </PriceSection>

            <InfoSection>
              <h3>Check-in</h3>
              <p>A partir das 15:00</p>
            </InfoSection>

            <InfoSection>
              <h3>Check-out</h3>
              <p>Até 11:00</p>
            </InfoSection>

            <InfoSection>
              <h3>Estadia</h3>
              <p>Mínimo de 1 noite + máximo de 11 noites</p>
            </InfoSection>

            <GuestSelector>
              <h3>Hóspedes</h3>
              {editingGuests ? (
                <div style={{ marginBottom: '16px' }}>
                  <EditableGuestField>
                    <label>Adultos</label>
                    <div className="input-group">
                      <input
                        type="number"
                        min="0"
                        value={guestDetails.adultos}
                        onChange={(e) => setGuestDetails({...guestDetails, adultos: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </EditableGuestField>
                  <EditableGuestField>
                    <label>Crianças</label>
                    <div className="input-group">
                      <input
                        type="number"
                        min="0"
                        value={guestDetails.criancas}
                        onChange={(e) => setGuestDetails({...guestDetails, criancas: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </EditableGuestField>
                  <EditableGuestField>
                    <label>Bebés</label>
                    <div className="input-group">
                      <input
                        type="number"
                        min="0"
                        value={guestDetails.bebes}
                        onChange={(e) => setGuestDetails({...guestDetails, bebes: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </EditableGuestField>
                  <EditableGuestField>
                    <label>Pets</label>
                    <div className="input-group">
                      <input
                        type="number"
                        min="0"
                        value={guestDetails.pets}
                        onChange={(e) => setGuestDetails({...guestDetails, pets: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </EditableGuestField>
                  <ActionButton 
                    className="primary" 
                    onClick={handleUpdateGuests}
                    style={{ marginBottom: '8px' }}
                  >
                    <FaSave style={{ marginRight: '8px' }} />
                    Salvar Alterações
                  </ActionButton>
                  <ActionButton 
                    className="secondary" 
                    onClick={() => setEditingGuests(false)}
                  >
                    Cancelar
                  </ActionButton>
                </div>
              ) : (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '16px', fontWeight: 700, color: '#333' }}>
                    Total de hóspedes: {guestDetails.adultos + guestDetails.criancas + guestDetails.bebes}
                  </div>
                  <div className="guest-grid">
                    <div className="guest-item">
                      <FaUsers className="icon" />
                      <div className="count">{guestDetails.adultos || 0}</div>
                      <div className="label">Adultos</div>
                    </div>
                    <div className="guest-item">
                      <FaChild className="icon" />
                      <div className="count">{guestDetails.criancas || 0}</div>
                      <div className="label">Crianças</div>
                    </div>
                    <div className="guest-item">
                      <FaBabyCarriage className="icon" />
                      <div className="count">{guestDetails.bebes || 0}</div>
                      <div className="label">Bebés</div>
                    </div>
                    <div className="guest-item">
                      <FaPaw className="icon" />
                      <div className="count">{guestDetails.pets || 0}</div>
                      <div className="label">Pets</div>
                    </div>
                  </div>
                  {!isGuestOnly && (
                    <ActionButton
                      className="secondary"
                      onClick={() => setEditingGuests(true)}
                      style={{ marginTop: '12px' }}
                    >
                      <FaEdit style={{ marginRight: '8px' }} />
                      Editar Hóspedes
                    </ActionButton>
                  )}
                </>
              )}
            </GuestSelector>

            <InfoSection>
              <h3>Contato do Hóspede</h3>
              <p>
                <FaEnvelope style={{ marginRight: '8px', color: '#ba824f' }} />
                {reservation.usuario?.emailUSUARIO || 'Não informado'}
              </p>
              <p style={{ marginTop: '8px' }}>
                <FaPhone style={{ marginRight: '8px', color: '#ba824f' }} />
                {reservation.usuario?.whatsappUSUARIO || reservation.usuario?.numeroprincipalUSUARIO || 'Não informado'}
              </p>
            </InfoSection>

            {!isGuestOnly && (
              <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                <ActionButton
                  className="danger"
                  onClick={handleDeleteReservation}
                >
                  <FaTrash style={{ marginRight: '8px' }} />
                  Cancelar Reserva
                </ActionButton>
              </div>
            )}
          </BookingSidebar>
        </div>
      </ContentGrid>
    </PageContainer>
  );
};

export default ReservationDetail;